import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CustomSelect } from "../../common/components/CustomSelect";
import logo from "../../../assets/app-logo.png";

// Pricing Constants based on Braindog Operational Finance Skill
const CONSTANTS = {
  gemini: {
    "flash-2.5": { input: 0.3, output: 2.5 }, // Per 1M tokens (Updated from Google AI Studio screenshot)
    "flash-3.0": { input: 0.5, output: 3.0 }, // Preview model
  },
};

interface SavedQuote {
  id: number;
  name: string;
  date: string;
  grandTotal: number;
  config?: any;
}

export const CostCalculator: React.FC = () => {
  const { t } = useTranslation();
  // Basic Settings
  const [projectName, setProjectName] = useState("New Reality Show");
  const [weeks, setWeeks] = useState(8);
  const [daysPerWeek, setDaysPerWeek] = useState(7);
  const [hoursPerDay, setHoursPerDay] = useState(24);

  // Feature settings
  const [audioStreams, setAudioStreams] = useState(4);
  const [videoStreams, setVideoStreams] = useState(1);
  const [enableTranscriptions, setEnableTranscriptions] = useState(true);
  const [enableVideoStorage, setEnableVideoStorage] = useState(true);
  const [enableGeminiAI, setEnableGeminiAI] = useState(true);

  // Video (Cloudflare R2 storage) details
  const [storyProducers, setStoryProducers] = useState(4);
  const [videoBitrateMbps, setVideoBitrateMbps] = useState(4); // libx264 CRF23, tope 6 Mbps
  const [retentionMonths, setRetentionMonths] = useState(1); // meses que los chunks quedan en R2

  // AI Parameters
  const [aiModel, setAiModel] = useState<"flash-2.5" | "flash-3.0">("flash-2.5");
  const [aiQueriesPerDay, setAiQueriesPerDay] = useState(100);
  const [avgInputTokens, setAvgInputTokens] = useState(40000); // Promedio de tokens enviados al modelo por pregunta
  const [avgOutputTokens, setAvgOutputTokens] = useState(500); // Promedio de tokens de respuesta

  // Fixed backend costs (mensuales)
  const [supabasePrice, setSupabasePrice] = useState(25);
  const [railwayPrice, setRailwayPrice] = useState(5);
  const [dropletPrice, setDropletPrice] = useState(6); // DigitalOcean s-1vcpu-1gb (egress WHEP en vivo)
  const [workerPrice, setWorkerPrice] = useState(5); // Cloudflare Worker + D1

  // Variable vendor costs
  const [sttEngine, setSttEngine] = useState<"deepgram" | "speechmatics">("deepgram");
  const [deepgramPrice, setDeepgramPrice] = useState(0.0097);
  const [speechmaticsPrice, setSpeechmaticsPrice] = useState(0.0093); // $0.56 per hour -> ~$0.0093 per min (Enhanced + Diarization)
  const [r2StoragePrice, setR2StoragePrice] = useState(0.015); // $/GB-mes (egress gratis)

  const [totals, setTotals] = useState({
    days: 0,
    totalHours: 0,
    sttTotal: 0,
    videoGB: 0,
    r2Storage: 0,
    geminiAICost: 0,
    fixedInfrastructure: 0,
    grandTotal: 0,
  });

  const [savedQuotes, setSavedQuotes] = useState<SavedQuote[]>([]);

  useEffect(() => {
    const stored = localStorage.getItem("braindog_cost_quotes");
    if (stored) {
      try {
        setSavedQuotes(JSON.parse(stored));
      } catch (e) {
        console.error("Error parsing saved quotes", e);
      }
    }
  }, []);

  const handleSaveQuote = () => {
    const newQuote: SavedQuote = {
      id: Date.now(),
      name: projectName,
      date: new Date().toLocaleDateString(),
      grandTotal: totals.grandTotal,
      config: {
        weeks,
        daysPerWeek,
        hoursPerDay,
        audioStreams,
        videoStreams,
        enableTranscriptions,
        enableVideoStorage,
        enableGeminiAI,
        storyProducers,
        videoBitrateMbps,
        retentionMonths,
        aiModel,
        aiQueriesPerDay,
        avgInputTokens,
        avgOutputTokens,
        supabasePrice,
        railwayPrice,
        dropletPrice,
        workerPrice,
        sttEngine,
        deepgramPrice,
        speechmaticsPrice,
        r2StoragePrice,
      },
    };
    const updated = [newQuote, ...savedQuotes];
    setSavedQuotes(updated);
    localStorage.setItem("braindog_cost_quotes", JSON.stringify(updated));
  };

  const handleLoadQuote = (quote: SavedQuote) => {
    if (!quote.config) return;
    setProjectName(quote.name);
    setWeeks(quote.config.weeks ?? 8);
    setDaysPerWeek(quote.config.daysPerWeek ?? 7);
    setHoursPerDay(quote.config.hoursPerDay ?? 24);
    setAudioStreams(quote.config.audioStreams ?? 4);
    setVideoStreams(quote.config.videoStreams ?? 1);
    setEnableTranscriptions(quote.config.enableTranscriptions ?? true);
    setEnableVideoStorage(quote.config.enableVideoStorage ?? true);
    setEnableGeminiAI(quote.config.enableGeminiAI ?? true);
    setStoryProducers(quote.config.storyProducers ?? 4);
    setVideoBitrateMbps(quote.config.videoBitrateMbps ?? 4);
    setRetentionMonths(quote.config.retentionMonths ?? 1);
    setAiModel(quote.config.aiModel ?? "flash-2.5");
    setAiQueriesPerDay(quote.config.aiQueriesPerDay ?? 100);
    setAvgInputTokens(quote.config.avgInputTokens ?? 40000);
    setAvgOutputTokens(quote.config.avgOutputTokens ?? 500);
    setSupabasePrice(quote.config.supabasePrice ?? 25);
    setRailwayPrice(quote.config.railwayPrice ?? 5);
    setDropletPrice(quote.config.dropletPrice ?? 6);
    setWorkerPrice(quote.config.workerPrice ?? 5);
    setSttEngine(quote.config.sttEngine ?? "deepgram");
    setDeepgramPrice(quote.config.deepgramPrice ?? 0.0097);
    setSpeechmaticsPrice(quote.config.speechmaticsPrice ?? 0.0093);
    setR2StoragePrice(quote.config.r2StoragePrice ?? 0.015);
  };

  const handleDeleteQuote = (id: number) => {
    const updated = savedQuotes.filter((q) => q.id !== id);
    setSavedQuotes(updated);
    localStorage.setItem("braindog_cost_quotes", JSON.stringify(updated));
  };

  const handlePrintPDF = () => {
    window.print();
  };

  useEffect(() => {
    calculateCosts();
  }, [
    weeks,
    daysPerWeek,
    hoursPerDay,
    audioStreams,
    videoStreams,
    enableTranscriptions,
    enableVideoStorage,
    enableGeminiAI,
    storyProducers,
    videoBitrateMbps,
    retentionMonths,
    aiQueriesPerDay,
    aiModel,
    avgInputTokens,
    avgOutputTokens,
    supabasePrice,
    railwayPrice,
    dropletPrice,
    workerPrice,
    sttEngine,
    deepgramPrice,
    speechmaticsPrice,
    r2StoragePrice,
  ]);

  const calculateCosts = () => {
    const totalDays = weeks * daysPerWeek;

    // --- AUDIO CALCULATIONS (STT) ---
    const totalAudioHours = totalDays * hoursPerDay * audioStreams;
    const totalAudioMinutes = totalAudioHours * 60;

    let sttCost = 0;
    if (enableTranscriptions) {
      const currentPrice = sttEngine === "speechmatics" ? speechmaticsPrice : deepgramPrice;
      sttCost = totalAudioMinutes * currentPrice;
    }

    // --- VIDEO STORAGE (Cloudflare R2) ---
    // El relay graba chunks HLS (libx264) y los sube a R2. R2 cobra por GB-mes;
    // el egress (que los story producers vean el video) es GRATIS. La entrega en
    // vivo (WHEP) corre en un droplet fijo, contabilizado en la infra fija.
    const totalVideoHours = totalDays * hoursPerDay * videoStreams;
    // bitrate (Mbps) → GB:  Mbit/s · 3600 s/h ÷ 8 = MB/h ;  ÷ 1000 = GB/h
    const videoGB = totalVideoHours * ((videoBitrateMbps * 3600) / 8 / 1000);

    let r2StorageCost = 0;
    if (enableVideoStorage) {
      // GB-mes: volumen pico retenido `retentionMonths`. (Ignora la rampa de
      // acumulación durante la grabación → estimación ligeramente conservadora.)
      r2StorageCost = videoGB * r2StoragePrice * retentionMonths;
    }

    // --- GEMINI AI (Generative Language API) ---
    let geminiCost = 0;
    if (enableGeminiAI) {
      const rates = CONSTANTS.gemini[aiModel];
      const inputRate = rates.input;
      const outputRate = rates.output;

      const costPerQuery =
        (avgInputTokens / 1000000) * inputRate + (avgOutputTokens / 1000000) * outputRate;
      geminiCost = aiQueriesPerDay * totalDays * costPerQuery;
    }

    // 3. Fixed Monthly Infrastructure (Supabase, Railway, Droplet DO, Worker+D1)
    // Convert weeks to months roughly
    const months = Math.ceil(weeks / 4.33) || 1;
    const fixedMonthly = (supabasePrice + railwayPrice + dropletPrice + workerPrice) * months;

    const grandTotal = sttCost + r2StorageCost + geminiCost + fixedMonthly;

    setTotals({
      days: totalDays,
      totalHours: totalAudioHours, // We use audio hours as the baseline for "Processed Project Hours"
      sttTotal: sttCost,
      videoGB,
      r2Storage: r2StorageCost,
      geminiAICost: geminiCost,
      fixedInfrastructure: fixedMonthly,
      grandTotal: grandTotal,
    });
  };

  const formatMoney = (val: number) => {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(val);
  };

  return (
    <div
      data-testid="cost-calculator"
      style={{ padding: "24px 40px", overflowY: "auto", height: "100%", boxSizing: "border-box" }}
      className="calculator-container"
    >
      <style>{`
                @media print {
                    body * {
                        visibility: hidden;
                    }
                    html, body, #root, div {
                        overflow: visible !important;
                        height: auto !important;
                        max-height: none !important;
                    }
                    .calculator-container, .calculator-container * {
                        visibility: visible;
                    }
                    .calculator-container {
                        position: absolute;
                        left: 0;
                        top: 0;
                        margin: 0;
                        padding: 0 !important;
                        width: 100%;
                    }
                    .no-print, .no-print * { 
                        display: none !important; 
                        visibility: hidden !important; 
                        height: 0 !important; 
                        margin: 0 !important; 
                        padding: 0 !important; 
                    }
                    .print-only-logo { display: block !important; }
                    .print-only-details { display: flex !important; }
                    body { background: #1a1a2e !important; color: #fff !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                    .print-quote-panel { 
                        width: 100% !important; 
                        border: 2px solid #555 !important; 
                        break-inside: avoid; 
                        box-shadow: none !important;
                        background: #111 !important;
                    }
                    .print-header { margin-bottom: 30px !important; }
                    
                    /* Reset the CSS Grid so it takes full width when printing */
                    .calc-grid {
                        display: block !important;
                    }
                }
            `}</style>

      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <div style={{ marginBottom: "30px" }} className="print-header">
          <h2 style={{ fontSize: "1.4rem", color: "var(--color-accent)", marginBottom: "8px" }}>
            <i className="bx bx-calculator" style={{ marginRight: "8px" }}></i>
            {t("admin.costCalculator.title")}
          </h2>
          <p style={{ color: "var(--color-text-muted)", fontSize: "0.85rem" }} className="no-print">
            {t("admin.costCalculator.description")}
          </p>
        </div>

        <div
          className="calc-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(300px, 1fr) 350px 250px",
            gap: "30px",
          }}
        >
          {/* Controls Panel */}
          <div
            data-testid="cost-calculator-controls"
            className="no-print"
            style={{
              background: "rgba(255, 255, 255, 0.03)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              borderRadius: "12px",
              padding: "24px",
            }}
          >
            <h3
              style={{
                fontSize: "1.1rem",
                marginBottom: "20px",
                color: "#fff",
                borderBottom: "1px solid rgba(255,255,255,0.1)",
                paddingBottom: "10px",
              }}
            >
              {t("admin.costCalculator.projectScope")}
            </h3>

            <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.75rem",
                    color: "#888",
                    marginBottom: "6px",
                  }}
                >
                  {t("admin.costCalculator.projectName")}
                </label>
                <input
                  data-testid="cost-calculator-project-name"
                  type="text"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  style={inputStyle}
                  placeholder="e.g. MasterChef Season 12"
                />
              </div>

              <div style={{ display: "flex", gap: "15px" }}>
                <div style={{ flex: 1 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "0.75rem",
                      color: "#888",
                      marginBottom: "6px",
                    }}
                  >
                    {t("admin.costCalculator.durationWeeks")}
                  </label>
                  <input
                    data-testid="cost-calculator-weeks"
                    type="number"
                    value={weeks}
                    onChange={(e) => setWeeks(Number(e.target.value))}
                    min={1}
                    max={52}
                    style={inputStyle}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "0.75rem",
                      color: "#888",
                      marginBottom: "6px",
                    }}
                  >
                    {t("admin.costCalculator.daysPerWeek")}
                  </label>
                  <input
                    data-testid="cost-calculator-days-per-week"
                    type="number"
                    value={daysPerWeek}
                    onChange={(e) => setDaysPerWeek(Number(e.target.value))}
                    min={1}
                    max={7}
                    style={inputStyle}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "0.75rem",
                      color: "#888",
                      marginBottom: "6px",
                    }}
                  >
                    {t("admin.costCalculator.hoursPerDay")}
                  </label>
                  <input
                    data-testid="cost-calculator-hours-per-day"
                    type="number"
                    value={hoursPerDay}
                    onChange={(e) => setHoursPerDay(Number(e.target.value))}
                    min={1}
                    max={24}
                    style={inputStyle}
                  />
                </div>
              </div>

              <div style={{ display: "flex", gap: "15px" }}>
                <div
                  style={{
                    flex: 1,
                    borderTop: "2px solid rgba(163, 230, 53, 0.5)",
                    paddingTop: "10px",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      fontSize: "0.85rem",
                      color: "#fff",
                      marginBottom: "8px",
                      fontWeight: "bold",
                    }}
                  >
                    <i className="bx bx-microphone" style={{ color: "#10B981" }}></i>{" "}
                    {t("admin.costCalculator.audioStreams")}
                  </label>
                  <input
                    data-testid="cost-calculator-audio-streams"
                    type="number"
                    value={audioStreams}
                    onChange={(e) => setAudioStreams(Number(e.target.value))}
                    min={1}
                    max={50}
                    style={inputStyle}
                  />
                  <div style={{ fontSize: "0.65rem", color: "#666", marginTop: "6px" }}>
                    {t("admin.costCalculator.audioStreamsHint")}
                  </div>
                </div>

                <div
                  style={{
                    flex: 1,
                    borderTop: "2px solid rgba(56, 189, 248, 0.5)",
                    paddingTop: "10px",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      fontSize: "0.85rem",
                      color: "#fff",
                      marginBottom: "8px",
                      fontWeight: "bold",
                    }}
                  >
                    <i className="bx bx-video" style={{ color: "#38BDF8" }}></i>{" "}
                    {t("admin.costCalculator.videoStreams")}
                  </label>
                  <input
                    data-testid="cost-calculator-video-streams"
                    type="number"
                    value={videoStreams}
                    onChange={(e) => setVideoStreams(Number(e.target.value))}
                    min={1}
                    max={20}
                    style={inputStyle}
                  />
                  <div style={{ fontSize: "0.65rem", color: "#666", marginTop: "6px" }}>
                    {t("admin.costCalculator.videoStreamsHint")}
                    <div
                      style={{
                        marginTop: "4px",
                        color: "#fff",
                        background: "rgba(255,255,255,0.05)",
                        padding: "4px 6px",
                        borderRadius: "4px",
                        border: "1px solid rgba(255,255,255,0.1)",
                      }}
                    >
                      <i
                        className="bx bx-check-shield"
                        style={{ color: "#38BDF8", marginRight: "4px" }}
                      ></i>
                      {t("admin.costCalculator.sweetSpot")}
                    </div>
                  </div>
                </div>
              </div>

              <h3
                style={{
                  fontSize: "1.1rem",
                  marginTop: "10px",
                  marginBottom: "10px",
                  color: "#fff",
                  borderBottom: "1px solid rgba(255,255,255,0.1)",
                  paddingBottom: "10px",
                }}
              >
                {t("admin.costCalculator.featuresUsage")}
              </h3>

              <label
                style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer" }}
              >
                <input
                  data-testid="cost-calculator-enable-transcriptions"
                  type="checkbox"
                  checked={enableTranscriptions}
                  onChange={(e) => setEnableTranscriptions(e.target.checked)}
                  style={{ transform: "scale(1.2)", accentColor: "var(--color-accent)" }}
                />
                <div>
                  <div style={{ fontSize: "0.85rem", color: "#fff" }}>
                    {t("admin.costCalculator.enableTranscription")}
                  </div>
                  <div style={{ fontSize: "0.65rem", color: "#888" }}>
                    {t("admin.costCalculator.transcribeAll")}
                  </div>
                </div>
              </label>

              {enableTranscriptions && (
                <div
                  style={{
                    background: "rgba(0,0,0,0.2)",
                    padding: "15px",
                    borderRadius: "8px",
                    border: "1px solid rgba(255,255,255,0.05)",
                    marginLeft: "30px",
                    marginTop: "10px",
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      fontSize: "0.75rem",
                      color: "#888",
                      marginBottom: "6px",
                    }}
                  >
                    {t("admin.costCalculator.sttEngine")}
                  </label>
                  <CustomSelect
                    value={sttEngine}
                    onChange={(val: string) => setSttEngine(val as "deepgram" | "speechmatics")}
                    options={[
                      { value: "deepgram", label: t("admin.costCalculator.deepgramOption") },
                      {
                        value: "speechmatics",
                        label: t("admin.costCalculator.speechmaticsOption"),
                      },
                    ]}
                    style={{ width: "100%" }}
                  />
                </div>
              )}

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  cursor: "pointer",
                  marginTop: "10px",
                }}
              >
                <input
                  data-testid="cost-calculator-enable-video-storage"
                  type="checkbox"
                  checked={enableVideoStorage}
                  onChange={(e) => setEnableVideoStorage(e.target.checked)}
                  style={{ transform: "scale(1.2)", accentColor: "var(--color-accent)" }}
                />
                <div>
                  <div style={{ fontSize: "0.85rem", color: "#fff" }}>
                    {t("admin.costCalculator.videoStorage")}
                  </div>
                  <div style={{ fontSize: "0.65rem", color: "#888" }}>
                    {t("admin.costCalculator.videoStorageDesc")}
                  </div>
                </div>
              </label>

              {enableVideoStorage && (
                <div
                  style={{
                    background: "rgba(0,0,0,0.2)",
                    padding: "15px",
                    borderRadius: "8px",
                    border: "1px solid rgba(255,255,255,0.05)",
                    marginLeft: "30px",
                  }}
                >
                  <div style={{ display: "flex", gap: "15px" }}>
                    <div style={{ flex: 1 }}>
                      <label
                        style={{
                          display: "block",
                          fontSize: "0.75rem",
                          color: "#888",
                          marginBottom: "6px",
                        }}
                      >
                        {t("admin.costCalculator.videoBitrate")}
                      </label>
                      <input
                        data-testid="cost-calculator-video-bitrate"
                        type="number"
                        value={videoBitrateMbps}
                        onChange={(e) => setVideoBitrateMbps(Number(e.target.value))}
                        min={0.5}
                        max={50}
                        step={0.5}
                        style={inputStyle}
                      />
                      <div style={{ fontSize: "0.6rem", color: "#666", marginTop: "6px" }}>
                        {t("admin.costCalculator.videoBitrateHint")}
                      </div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <label
                        style={{
                          display: "block",
                          fontSize: "0.75rem",
                          color: "#888",
                          marginBottom: "6px",
                        }}
                      >
                        {t("admin.costCalculator.retentionMonths")}
                      </label>
                      <input
                        data-testid="cost-calculator-retention-months"
                        type="number"
                        value={retentionMonths}
                        onChange={(e) => setRetentionMonths(Number(e.target.value))}
                        min={1}
                        max={60}
                        style={inputStyle}
                      />
                      <div style={{ fontSize: "0.6rem", color: "#666", marginTop: "6px" }}>
                        {t("admin.costCalculator.retentionHint")}
                      </div>
                    </div>
                  </div>

                  <label
                    style={{
                      display: "block",
                      fontSize: "0.75rem",
                      color: "#888",
                      marginBottom: "6px",
                      marginTop: "15px",
                    }}
                  >
                    {t("admin.costCalculator.storyProducers")}
                  </label>
                  <input
                    data-testid="cost-calculator-story-producers"
                    type="number"
                    value={storyProducers}
                    onChange={(e) => setStoryProducers(Number(e.target.value))}
                    min={1}
                    max={50}
                    style={inputStyle}
                  />
                </div>
              )}

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  cursor: "pointer",
                  marginTop: "10px",
                }}
              >
                <input
                  data-testid="cost-calculator-enable-ai"
                  type="checkbox"
                  checked={enableGeminiAI}
                  onChange={(e) => setEnableGeminiAI(e.target.checked)}
                  style={{ transform: "scale(1.2)", accentColor: "var(--color-accent)" }}
                />
                <div>
                  <div style={{ fontSize: "0.85rem", color: "#fff" }}>
                    {t("admin.costCalculator.brainEngine")}
                  </div>
                  <div style={{ fontSize: "0.65rem", color: "#888" }}>
                    {t("admin.costCalculator.naturalLanguageQueries")}
                  </div>
                </div>
              </label>

              {enableGeminiAI && (
                <div
                  style={{
                    background: "rgba(0,0,0,0.2)",
                    padding: "15px",
                    borderRadius: "8px",
                    border: "1px solid rgba(255,255,255,0.05)",
                    marginLeft: "30px",
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      fontSize: "0.75rem",
                      color: "#888",
                      marginBottom: "6px",
                    }}
                  >
                    {t("admin.costCalculator.aiModel")}
                  </label>
                  <CustomSelect
                    value={aiModel}
                    onChange={(val: string) => setAiModel(val as any)}
                    options={[
                      { value: "flash-2.5", label: t("admin.costCalculator.geminiFlash") },
                      { value: "flash-3.0", label: t("admin.costCalculator.gemini3Flash") },
                    ]}
                    style={{ marginBottom: "15px", width: "100%" }}
                  />

                  <div style={{ display: "flex", gap: "15px", marginBottom: "15px" }}>
                    <div style={{ flex: 1 }}>
                      <label
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: "0.75rem",
                          color: "#888",
                          marginBottom: "6px",
                        }}
                      >
                        <span>{t("admin.costCalculator.inputContext")}</span>
                        <span style={{ color: "#fff", fontWeight: "bold" }}>
                          {avgInputTokens.toLocaleString()}
                        </span>
                      </label>
                      <input
                        data-testid="cost-calculator-input-tokens"
                        type="range"
                        value={avgInputTokens}
                        onChange={(e) => setAvgInputTokens(Number(e.target.value))}
                        min={10000}
                        max={2000000}
                        step={10000}
                        style={{ width: "100%", accentColor: "var(--color-accent)" }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: "0.75rem",
                          color: "#888",
                          marginBottom: "6px",
                        }}
                      >
                        <span>{t("admin.costCalculator.estimatedOutput")}</span>
                        <span style={{ color: "#fff", fontWeight: "bold" }}>
                          {avgOutputTokens.toLocaleString()}
                        </span>
                      </label>
                      <input
                        data-testid="cost-calculator-output-tokens"
                        type="range"
                        value={avgOutputTokens}
                        onChange={(e) => setAvgOutputTokens(Number(e.target.value))}
                        min={100}
                        max={8192}
                        step={100}
                        style={{ width: "100%", accentColor: "var(--color-accent)" }}
                      />
                    </div>
                  </div>

                  <div style={{ marginBottom: "15px" }}>
                    <label
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: "0.75rem",
                        color: "#888",
                        marginBottom: "6px",
                      }}
                    >
                      <span>{t("admin.costCalculator.estimatedQueries")}</span>
                      <span style={{ color: "#fff", fontWeight: "bold" }}>
                        {aiQueriesPerDay.toLocaleString()}
                      </span>
                    </label>
                    <input
                      data-testid="cost-calculator-queries-per-day"
                      type="range"
                      value={aiQueriesPerDay}
                      onChange={(e) => setAiQueriesPerDay(Number(e.target.value))}
                      min={5}
                      max={500}
                      step={5}
                      style={{ width: "100%", accentColor: "var(--color-accent)" }}
                    />
                  </div>

                  <div
                    style={{
                      fontSize: "0.65rem",
                      color: "#555",
                      marginTop: "8px",
                      padding: "6px",
                      background: "rgba(255,255,255,0.03)",
                      borderRadius: "4px",
                    }}
                  >
                    {t("admin.costCalculator.costPerQuery")}{" "}
                    <strong>
                      $
                      {(
                        (avgInputTokens / 1000000) * CONSTANTS.gemini[aiModel].input +
                        (avgOutputTokens / 1000000) * CONSTANTS.gemini[aiModel].output
                      ).toFixed(4)}
                    </strong>
                  </div>
                </div>
              )}

              <h3
                style={{
                  fontSize: "1.1rem",
                  marginTop: "20px",
                  marginBottom: "10px",
                  color: "#fff",
                  borderBottom: "1px solid rgba(255,255,255,0.1)",
                  paddingBottom: "10px",
                }}
              >
                {t("admin.costCalculator.vendorPricing")}
              </h3>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr 1fr",
                  gap: "15px",
                  marginBottom: "15px",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      fontSize: "0.85rem",
                      color: "#fff",
                      marginBottom: "8px",
                      fontWeight: "bold",
                    }}
                  >
                    <i className="bx bx-data" style={{ color: "#10B981" }}></i>{" "}
                    {t("admin.costCalculator.supabaseMonthly")}
                  </label>
                  <input
                    data-testid="cost-calculator-supabase-price"
                    type="number"
                    value={supabasePrice}
                    onChange={(e) => setSupabasePrice(Number(e.target.value))}
                    min={0}
                    max={1000}
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      fontSize: "0.85rem",
                      color: "#fff",
                      marginBottom: "8px",
                      fontWeight: "bold",
                    }}
                  >
                    <i className="bx bx-server" style={{ color: "#FCD34D" }}></i>{" "}
                    {t("admin.costCalculator.railwayMonthly")}
                  </label>
                  <input
                    data-testid="cost-calculator-railway-price"
                    type="number"
                    value={railwayPrice}
                    onChange={(e) => setRailwayPrice(Number(e.target.value))}
                    min={0}
                    max={1000}
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      fontSize: "0.85rem",
                      color: "#fff",
                      marginBottom: "8px",
                      fontWeight: "bold",
                    }}
                  >
                    <i className="bx bx-cloud" style={{ color: "#38BDF8" }}></i>{" "}
                    {t("admin.costCalculator.dropletMonthly")}
                  </label>
                  <input
                    data-testid="cost-calculator-droplet-price"
                    type="number"
                    value={dropletPrice}
                    onChange={(e) => setDropletPrice(Number(e.target.value))}
                    min={0}
                    max={1000}
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      fontSize: "0.85rem",
                      color: "#fff",
                      marginBottom: "8px",
                      fontWeight: "bold",
                    }}
                  >
                    <i className="bx bx-cloud-lightning" style={{ color: "#F97316" }}></i>{" "}
                    {t("admin.costCalculator.workerMonthly")}
                  </label>
                  <input
                    data-testid="cost-calculator-worker-price"
                    type="number"
                    value={workerPrice}
                    onChange={(e) => setWorkerPrice(Number(e.target.value))}
                    min={0}
                    max={1000}
                    style={inputStyle}
                  />
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "15px" }}>
                <div>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      fontSize: "0.75rem",
                      color: "#888",
                      marginBottom: "8px",
                    }}
                  >
                    {t("admin.costCalculator.deepgramPerMin")}
                  </label>
                  <input
                    data-testid="cost-calculator-deepgram-price"
                    type="number"
                    step="0.0001"
                    value={deepgramPrice}
                    onChange={(e) => setDeepgramPrice(Number(e.target.value))}
                    min={0}
                    max={1}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      fontSize: "0.75rem",
                      color: "#888",
                      marginBottom: "8px",
                    }}
                  >
                    {t("admin.costCalculator.speechmaticsPerMin")}
                  </label>
                  <input
                    data-testid="cost-calculator-speechmatics-price"
                    type="number"
                    step="0.0001"
                    value={speechmaticsPrice}
                    onChange={(e) => setSpeechmaticsPrice(Number(e.target.value))}
                    min={0}
                    max={1}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      fontSize: "0.75rem",
                      color: "#888",
                      marginBottom: "8px",
                    }}
                  >
                    {t("admin.costCalculator.r2StoragePerGb")}
                  </label>
                  <input
                    data-testid="cost-calculator-r2-storage-price"
                    type="number"
                    step="0.001"
                    value={r2StoragePrice}
                    onChange={(e) => setR2StoragePrice(Number(e.target.value))}
                    min={0}
                    max={1}
                    style={inputStyle}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Results Panel */}
          <div
            data-testid="cost-calculator-results"
            className="print-quote-panel"
            style={{
              background: "linear-gradient(180deg, rgba(30,30,30,1) 0%, rgba(20,20,20,1) 100%)",
              border: "1px solid rgba(163, 230, 53, 0.3)",
              borderRadius: "12px",
              padding: "24px",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
              height: "fit-content",
            }}
          >
            <div className="print-only-logo" style={{ display: "none", marginBottom: "20px" }}>
              <img src={logo} alt="Braindog Logo" style={{ width: "180px" }} />
            </div>
            <h3
              style={{
                fontSize: "1.2rem",
                marginBottom: "5px",
                color: "var(--color-accent)",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <i className="bx bx-receipt"></i> {t("admin.costCalculator.estimationQuote")}
            </h3>
            <div
              style={{
                fontSize: "0.85rem",
                color: "#fff",
                fontWeight: 600,
                marginBottom: "20px",
                paddingBottom: "15px",
                borderBottom: "1px solid rgba(255,255,255,0.1)",
              }}
            >
              {projectName || "Untitled Project"}
            </div>

            <div
              className="print-only-details"
              style={{
                display: "none",
                flexDirection: "column",
                gap: "10px",
                marginBottom: "20px",
                paddingBottom: "15px",
                borderBottom: "1px solid rgba(255,255,255,0.1)",
                fontSize: "0.8rem",
                color: "#ccc",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>{t("admin.costCalculator.projectDuration")}</span>
                <span style={{ fontWeight: "bold", color: "#fff" }}>
                  {weeks} Weeks ({daysPerWeek} days/week, {hoursPerDay} hrs/day)
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>{t("admin.costCalculator.audioStreams")}:</span>
                <span style={{ fontWeight: "bold", color: "#fff" }}>
                  {audioStreams} Sources (
                  {sttEngine === "speechmatics"
                    ? "Speechmatics Enhanced + Diarize"
                    : "Deepgram Nova-3 + Diarize"}
                  )
                </span>
              </div>
              {enableVideoStorage && (
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>{t("admin.costCalculator.videoProxies")}</span>
                  <span style={{ fontWeight: "bold", color: "#fff" }}>
                    {videoStreams} Streams · {videoBitrateMbps} Mbps ·{" "}
                    {totals.videoGB.toLocaleString(undefined, { maximumFractionDigits: 0 })} GB
                  </span>
                </div>
              )}
              {enableGeminiAI && (
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>{t("admin.costCalculator.brainAIEngine")}</span>
                  <span style={{ fontWeight: "bold", color: "#fff" }}>
                    {aiModel} (~{aiQueriesPerDay} queries/day)
                  </span>
                </div>
              )}
            </div>

            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "16px" }}>
              <div style={rowStyle}>
                <span style={labelStyle}>{t("admin.costCalculator.totalProcessedAudio")}</span>
                <span style={valueStyle}>{totals.totalHours.toLocaleString()} hours</span>
              </div>

              <div
                style={{ height: "1px", background: "rgba(255,255,255,0.1)", margin: "8px 0" }}
              />

              {enableTranscriptions && (
                <div style={rowStyle}>
                  <span style={labelStyle}>{t("admin.costCalculator.transcriptions")} </span>
                  <span style={valueStyle}>{formatMoney(totals.sttTotal)}</span>
                </div>
              )}

              {enableVideoStorage && (
                <>
                  <div style={{ ...rowStyle, alignItems: "center" }}>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span style={labelStyle}>{t("admin.costCalculator.r2Storage")}</span>
                      <span style={{ fontSize: "0.65rem", color: "#666", marginTop: "2px" }}>
                        {totals.videoGB.toLocaleString(undefined, { maximumFractionDigits: 0 })} GB
                        · {retentionMonths}{" "}
                        {retentionMonths === 1
                          ? t("admin.costCalculator.monthSingular")
                          : t("admin.costCalculator.monthPlural")}
                      </span>
                    </div>
                    <span style={valueStyle}>{formatMoney(totals.r2Storage)}</span>
                  </div>
                  <div style={rowStyle}>
                    <span style={labelStyle}>{t("admin.costCalculator.egressFree")}</span>
                    <span style={{ ...valueStyle, color: "#10B981" }}>
                      {t("admin.costCalculator.freeLabel")}
                    </span>
                  </div>
                </>
              )}

              {enableGeminiAI && (
                <div style={rowStyle}>
                  <span style={labelStyle}>{t("admin.costCalculator.geminiQueries")}</span>
                  <span style={valueStyle}>{formatMoney(totals.geminiAICost)}</span>
                </div>
              )}

              <div style={{ ...rowStyle, alignItems: "center" }}>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <span style={labelStyle}>{t("admin.costCalculator.fixedBackend")}</span>
                  <span style={{ fontSize: "0.65rem", color: "#666", marginTop: "2px" }}>
                    {t("admin.costCalculator.fixedBackendDesc", {
                      supabase: supabasePrice,
                      railway: railwayPrice,
                      droplet: dropletPrice,
                      worker: workerPrice,
                    })}
                  </span>
                </div>
                <span style={valueStyle}>{formatMoney(totals.fixedInfrastructure)}</span>
              </div>
            </div>

            <div
              style={{
                marginTop: "30px",
                background: "rgba(163, 230, 53, 0.1)",
                padding: "20px",
                borderRadius: "12px",
                border: "1px solid rgba(163, 230, 53, 0.4)",
              }}
            >
              <div
                style={{
                  fontSize: "0.8rem",
                  color: "var(--color-accent)",
                  fontWeight: 800,
                  textTransform: "uppercase",
                  marginBottom: "10px",
                }}
              >
                {t("admin.costCalculator.totalProjectedCost")}
              </div>
              <div
                data-testid="cost-calculator-grand-total"
                style={{ fontSize: "2.5rem", fontWeight: 900, color: "#fff", lineHeight: 1 }}
              >
                {formatMoney(totals.grandTotal)}
              </div>
              <div style={{ fontSize: "0.7rem", color: "#aaa", marginTop: "10px" }}>
                {t("admin.costCalculator.safeMargin")}
                <br />
                {formatMoney(totals.grandTotal * 0.9)} - {formatMoney(totals.grandTotal * 1.1)}
              </div>
            </div>

            <div className="no-print" style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
              <button
                data-testid="cost-calculator-save-quote"
                onClick={handleSaveQuote}
                style={{
                  flex: 1,
                  padding: "12px",
                  background: "var(--color-accent)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontWeight: "bold",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "8px",
                  transition: "filter 0.2s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.filter = "brightness(1.2)")}
                onMouseLeave={(e) => (e.currentTarget.style.filter = "brightness(1)")}
              >
                <i className="bx bx-save"></i> {t("admin.costCalculator.saveQuote")}
              </button>
              <button
                data-testid="cost-calculator-export-pdf"
                onClick={handlePrintPDF}
                style={{
                  flex: 1,
                  padding: "12px",
                  background: "rgba(255,255,255,0.1)",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.2)",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontWeight: "bold",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "8px",
                  transition: "background 0.2s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.15)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.1)")}
              >
                <i className="bx bx-printer"></i> {t("admin.costCalculator.exportPdf")}
              </button>
            </div>
          </div>

          {/* Saved Quotes Sidebar */}
          <div
            data-testid="cost-calculator-saved-quotes"
            className="no-print"
            style={{
              background: "rgba(0, 0, 0, 0.2)",
              border: "1px solid rgba(255, 255, 255, 0.05)",
              borderRadius: "12px",
              padding: "20px",
              height: "fit-content",
            }}
          >
            <h3
              style={{
                fontSize: "1rem",
                marginBottom: "15px",
                color: "#ccc",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <i className="bx bx-history"></i> {t("admin.costCalculator.savedQuotes")}
            </h3>

            {savedQuotes.length === 0 ? (
              <div
                style={{
                  fontSize: "0.8rem",
                  color: "#666",
                  textAlign: "center",
                  padding: "20px 0",
                }}
              >
                {t("admin.costCalculator.noSavedQuotes")}
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {savedQuotes.map((quote) => (
                  <div
                    key={quote.id}
                    onClick={() => handleLoadQuote(quote)}
                    style={{
                      background: "rgba(255,255,255,0.03)",
                      padding: "12px",
                      borderRadius: "8px",
                      border: "1px solid rgba(255,255,255,0.05)",
                      position: "relative",
                      cursor: quote.config ? "pointer" : "default",
                      transition: "background 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      if (quote.config) e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                    }}
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "rgba(255,255,255,0.03)")
                    }
                  >
                    <div
                      style={{
                        fontSize: "0.85rem",
                        color: "#fff",
                        fontWeight: "bold",
                        marginBottom: "4px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        paddingRight: "24px",
                      }}
                    >
                      {quote.name || "Untitled"}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ fontSize: "0.7rem", color: "#888" }}>{quote.date}</span>
                      <span
                        style={{
                          fontSize: "0.9rem",
                          color: "var(--color-accent)",
                          fontWeight: "bold",
                        }}
                      >
                        {formatMoney(quote.grandTotal)}
                      </span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteQuote(quote.id);
                      }}
                      style={{
                        position: "absolute",
                        top: "8px",
                        right: "8px",
                        background: "transparent",
                        border: "none",
                        color: "#ff4444",
                        cursor: "pointer",
                        padding: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "4px",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "rgba(255,68,68,0.1)")
                      }
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      title="Delete Quote"
                    >
                      <i className="bx bx-trash"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const inputStyle = {
  width: "100%",
  background: "rgba(0,0,0,0.3)",
  border: "1px solid rgba(255,255,255,0.15)",
  padding: "8px 12px",
  borderRadius: "6px",
  color: "#fff",
  fontSize: "0.9rem",
  outline: "none",
  boxSizing: "border-box" as const,
};

const rowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const labelStyle = {
  fontSize: "0.85rem",
  color: "#aaa",
};

const valueStyle = {
  fontSize: "0.95rem",
  fontWeight: 600,
  color: "#eee",
};
